import React, { useState, CSSProperties } from 'react'
import { useGlobal } from '../../hooks/useGlobal'
import { useResponsive, useScrollAtTop } from '../../hooks/useResponsive'
import { Link } from 'react-router-dom'

/**
 * Componente Navbar responsivo unificado
 * 
 * Funciona tanto para desktop quanto para mobile, ajustando automaticamente
 * o layout baseado no tamanho da tela.
 * 
 * Ordem na tela:
 * 1. HeaderEstadual - z-index 9999
 * 2. LogoSection - z-index 998
 * 3. Navbar (este componente) - z-index 997
 * 
 * Comportamento:
 * - Quando no topo: fica abaixo do LogoSection
 * - Quando scroll: sobe e fica logo abaixo do HeaderEstadual
 */
export function Navbar() {
  const { data, loading, error } = useGlobal()
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  
  const { 
    isSmallMobile, 
    isMobile,
    headerEstadualHeight,
    logoSectionHeight,
    navbarHeight 
  } = useResponsive()
  
  const isAtTop = useScrollAtTop()

  if (loading || error || !data?.Navbar) return null

  const navbar = data.Navbar
  const logo = navbar.title || ''
  const menu = navbar.items || []

  // Cálculos de dimensões baseados no breakpoint
  const fontSize = isSmallMobile ? 12 : isMobile ? 14 : 18
  const logoFontSize = isSmallMobile ? 18 : isMobile ? 22 : 28
  const menuGap = isSmallMobile ? 8 : isMobile ? 16 : 32
  const lateralPadding = isSmallMobile ? '8px' : isMobile ? '16px' : 'clamp(16px, 5vw, 7vw)'
  const menuItemHeight = isSmallMobile ? 28 : isMobile ? 32 : 40

  // Posição top baseada no scroll
  // Quando no topo: abaixo do HeaderEstadual + LogoSection
  // Quando scroll: abaixo do HeaderEstadual
  const topPosition = isAtTop 
    ? headerEstadualHeight + logoSectionHeight
    : headerEstadualHeight

  // Estilos
  const navStyle: CSSProperties = {
    position: 'fixed',
    top: `${topPosition}px`,
    left: 0,
    width: '100%',
    zIndex: 997,
    background: 'white',
    padding: 0,
    height: navbarHeight,
    display: 'flex',
    alignItems: 'center',
    transition: 'top 0.3s ease-in-out',
  }

  const innerContainerStyle: CSSProperties = {
    width: '100%',
    maxWidth: 1700,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMobile ? 'center' : 'space-between',
    padding: `0 ${lateralPadding}`,
    height: navbarHeight,
    gap: menuGap
  }

  const logoStyle: CSSProperties = {
    fontWeight: 800,
    fontSize: logoFontSize,
    color: '#1976d2',
    letterSpacing: 1,
    whiteSpace: 'nowrap',
    flexShrink: 0
  }

  const menuContainerStyle: CSSProperties = {
    display: 'flex',
    gap: menuGap,
    alignItems: 'center',
    justifyContent: 'center',
    flex: isMobile ? 'none' : 1,
    flexWrap: isMobile ? 'nowrap' : 'nowrap',
    overflow: isMobile ? 'auto' : 'visible'
  }

  const getMenuItemStyle = (idx: number, item: { active?: boolean }): CSSProperties => ({
    color: '#1976d2',
    fontWeight: 500,
    fontSize: fontSize,
    textDecoration: 'none',
    padding: '0 4px',
    transition: 'border-bottom 0.2s, opacity 0.2s',
    height: menuItemHeight,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    // Reason: Aplica estilo visual quando o item está em hover
    borderBottom: hoveredIdx === idx ? '2px solid #1976d2' : '2px solid transparent',
    opacity: hoveredIdx !== null && hoveredIdx !== idx ? 0.7 : 1
  })

  // Espaçador invisível para manter o menu centralizado (apenas desktop)
  const spacerStyle: CSSProperties = {
    width: 'fit-content',
    visibility: 'hidden',
    flexShrink: 0
  }

  return (
    <nav style={navStyle}>
      <div className="navbar-inner" style={innerContainerStyle}>
        {/* Logo SICAR - Alinhado à esquerda (apenas desktop) */}
        {!isMobile && (
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
            <div style={logoStyle}>
              {logo}
            </div>
          </Link>
        )}

        {/* Menu */}
        <div className="navbar-menu" style={menuContainerStyle}>
          {menu.map((item: { title: string; link: string; active?: boolean }, idx: number) => (
            <a
              key={idx}
              href={item.link}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={getMenuItemStyle(idx, item)}
            >
              {item.title}
            </a>
          ))}
        </div>

        {/* Espaçador invisível para manter o menu centralizado (apenas desktop) */}
        {!isMobile && (
          <div style={spacerStyle}>
            <div style={{ ...logoStyle, color: 'transparent' }}>
              {logo}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
